import { ReactElement, SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { EContainerIconSize, EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { storageKeyName } from 'utils/generic/storage-key-name';
import {
  Game,
  IToggleSportMatchAction
} from 'interfaces/sportsbook-data-levels';
import { EventAction } from 'components/EventAction';
import { GlobalIcon } from 'components/GlobalIcon';
import { useOpenLoginPopup } from 'hooks/account/useOpenLoginPopup';
import { toggleFavorite } from 'store/actions/fav-data';
import {
  getFavoriteActiveStateById,
  getFavoriteMatchIds
} from 'store/selectors/fav-data';
import { getIsLoggedIn } from 'store/selectors/user-data';
import './index.less';

type Props = {
  game: Pick<Game, 'id' | 'type'>;
  size?: EIconSize._16 | EIconSize._20;
  ghost?: boolean;
  className?: string;
  iconMargin?: 'left' | 'right' | null;
  iconContainerSize?:
    | EContainerIconSize.SMALL
    | EContainerIconSize.MIDDLE
    | EContainerIconSize.LARGE;
  externalCallback?: () => void;
};

export const FavoriteButtonGame = ({
  game,
  size,
  className,
  iconMargin = 'left',
  ghost,
  iconContainerSize,
  externalCallback
}: Props): ReactElement | null => {
  const { id } = game;
  const dispatch = useDispatch();
  const favoriteMatchIds = useSelector(getFavoriteMatchIds);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const openLoginPopup = useOpenLoginPopup();

  const gameType = (
    game.type === 1 ? 'live' : 'prematch'
  ) as IToggleSportMatchAction['gameType'];

  const favoriteSelectorOptions: Omit<IToggleSportMatchAction, 'entity'> = {
    gameType,
    groupKey: favoriteMatchIds.esport[gameType].includes(id)
      ? 'esport'
      : 'sportsbook'
  };

  const active = useSelector(
    getFavoriteActiveStateById(game.id, favoriteSelectorOptions.groupKey)
  );

  const handleFavGame = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isLoggedIn && SpringConfigs.USER_PERSONALIZATION_ENABLED) {
      LocalStorage.setItem(
        storageKeyName('account', 'UNSIGNED_FAV_SPORT_GAME'),
        JSON.stringify({
          groupKey: favoriteSelectorOptions.groupKey,
          gameType,
          id
        })
      );
      openLoginPopup();
    } else {
      dispatch(toggleFavorite({ ...favoriteSelectorOptions, entity: id }));
    }

    externalCallback?.();
  };

  return (
    <EventAction
      margin={iconMargin}
      iconContainerSize={iconContainerSize}
      ghost={ghost}
      active={active}
      className={className}
      onClick={handleFavGame}
      icon={
        <GlobalIcon
          lib="generic"
          name="favorite"
          theme="default"
          size={size}
          className="gameFavIcon"
        />
      }
    />
  );
};
